import React, { useMemo, useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Button,
  Cell,
  colors,
  LayoutBand,
  Row,
  Separator,
  Skeleton,
  DescriptionTooltip,
  H5,
  Subheading,
} from '@fs/zion-ui'
import { useLazyQuery } from '@apollo/client/react'
import { useUser } from '@fs/zion-user'
import queries from '../../queries'

import { useGroupData, useImageOrganizeModes, useImageOrganizeActions } from '../../hooks'
import SwitchItemButton from '../groupDataHeader/SwitchItemButton'

export default function ImageOrganizeHeader({ onEditClick, hideEditButton = false }) {
  const [t] = useTranslation()
  const { itemNumber, itemCount, loading, initialGroup, initialChildId, groupIndex } = useGroupData()
  const { isMetadataSingleMode, isMetadataAllMode, isDivideMode } = useImageOrganizeModes()
  const [initialItemNumber] = useState(itemNumber)
  const { cisId } = useUser()

  const currentGroup = useMemo(() => {
    return initialGroup?.childGroups?.[groupIndex] ?? initialGroup
  }, [initialGroup, groupIndex])

  const { getSkippedStatus, dividerOffsets } = useImageOrganizeActions()
  const itemStatus = useMemo(() => getSkippedStatus(currentGroup?.id), [currentGroup, getSkippedStatus])
  const isDividedGroup = isDivideMode ? dividerOffsets?.length > 1 : itemCount > 1

  const [fetchImageOrganizeStatus, { data: statusData }] = useLazyQuery(queries.imageOrganizeStatus())
  useEffect(() => {
    if (currentGroup?.id) {
      fetchImageOrganizeStatus({ variables: { rmsId: currentGroup.id } })
    }
  }, [currentGroup, fetchImageOrganizeStatus])

  const currentGroupStatus = useMemo(() => statusData?.imageOrganizeStatus?.correctMetadata?.status, [statusData])

  const disableEdit =
    (isMetadataSingleMode && currentGroup?.id !== initialChildId) ||
    currentGroupStatus === 'Completed' ||
    currentGroupStatus === 'Skipped' ||
    itemStatus === true

  const transl = {
    edit: t('ImageOrganizeHeader.edit', 'Edit'),
    heading: t('ImageOrganizeHeader.heading', 'Item {itemNumber}', {
      itemNumber,
    }),
    subHeading: t('ImageOrganizeHeader.subHeading', 'Metadata describing Item {itemNumber} of {itemCount}', {
      itemNumber,
      itemCount: isDivideMode ? dividerOffsets?.length : itemCount,
    }),
    viewAnotherItem: t('ImageOrganizeHeader.viewAnotherItem', 'View Another Item'),
    editDisabledHeader: t('ImageOrganizeHeader.editDisabledHeader', 'Task Not Available'),
    editDisabledBody: t(
      'ImageOrganizeHeader.editDisabledBoydy',
      'You only reserved the Correct Metadata task for Item {initialItemNumber}.',
      { initialItemNumber }
    ),
    resetCorrectMessage: t(
      'ImageOrganize.resetItemCorrectStatusMessage',
      'This item is marked correct. To enable editing, reset the status in the bottom-right corner of the screen.'
    ),
    resetSkipMessage: t(
      'ImageOrganize.resetItemSkipStatusMessage',
      'You skipped this item. To enable editing, reset the status in the bottom-right corner of the screen.'
    ),
    editingNotAvavilable: t('ImageOrgainize.resetToEdit', 'Reset Status to Edit'),
    taskNotAvailable: t('ImageOrganise.taskNotAvailable', 'Task Not Available'),
    wholeGroup: t('ImageOrganizeHeader.wholeGroup', 'Metadata describing the entire group of images'),
  }

  const disabledReason = useMemo(() => {
    if (isMetadataSingleMode && currentGroup?.id !== initialChildId) return transl.editDisabledBody
    if (itemStatus) return transl.resetSkipMessage
    if (currentGroupStatus === 'Completed') return transl.resetCorrectMessage
    return transl.resetSkipMessage
  }, [
    currentGroupStatus,
    initialChildId,
    isMetadataSingleMode,
    itemStatus,
    transl.editDisabledBody,
    transl.resetCorrectMessage,
    transl.resetSkipMessage,
    currentGroup,
  ])

  const disabledHeader = useMemo(() => {
    if (isMetadataSingleMode && currentGroup?.id !== initialChildId) return transl.taskNotAvailable
    return transl.editingNotAvavilable
  }, [currentGroup, initialChildId, isMetadataSingleMode, transl.editingNotAvavilable, transl.taskNotAvailable])

  const isInProgressByAnotherUser = useMemo(
    () => currentGroupStatus === 'In Progress' && statusData?.imageOrganizeStatus?.correctMetadata?.userId !== cisId,
    [cisId, currentGroupStatus, statusData]
  )

  return (
    <LayoutBand color={colors.gray02}>
      {loading ? (
        <LoadingView />
      ) : (
        <Row gutters="xxs">
          <Cell growX>
            <Separator size="xxs" />
            <H5 supplementary>
              {transl.heading}
              <Subheading>{isDividedGroup ? transl.subHeading : transl.wholeGroup}</Subheading>
            </H5>
            <SwitchItemButton />
          </Cell>
          {(isMetadataSingleMode || isMetadataAllMode) && !hideEditButton && !isInProgressByAnotherUser && (
            <Cell noShrinkX>
              <Separator size="xxs" />
              {disableEdit && (
                <DescriptionTooltip
                  target={
                    <Button emphasis="medium" onClick={onEditClick} disabled>
                      {transl.edit}
                    </Button>
                  }
                  label={disabledHeader}
                  description={disabledReason}
                />
              )}
              {!disableEdit && (
                <Button emphasis="medium" onClick={onEditClick}>
                  {transl.edit}
                </Button>
              )}
            </Cell>
          )}
        </Row>
      )}
    </LayoutBand>
  )
}

function LoadingView() {
  return (
    <div style={{ padding: '8px' }}>
      <Separator size="xxs" />
      <Row gutters="xxs">
        <Cell growX>
          <Skeleton.H5 subheadingLineCount={1} />
          <Skeleton.Button />
        </Cell>
        <Cell>
          <Skeleton.Button />
        </Cell>
      </Row>
      <Separator size="xxs" />
    </div>
  )
}
